import sortBy from 'lodash/sortBy';
import { format, parseISO } from 'date-fns';
import { createSelector } from 'reselect';
import { SERVER_FORMAT_DATE } from '@ts/utils/constants';
import * as appSelectors from './app';

const key = 'search';
const domains = {
    dateFrom: (state) => state.global[key].dateFrom,
    dateTo: (state) => state.global[key].dateTo,
    destinationId: (state) => state.global[key].destinationId,
    destinationCategoryId: (state) => state.global[key].destinationCategoryId,
};

const selectorDateFrom = createSelector(
    [domains.dateFrom],
    (dateFrom) => (dateFrom && parseISO(dateFrom)) || null
);

const selectorDateTo = createSelector(
    [domains.dateTo],
    (dateTo) => (dateTo && parseISO(dateTo)) || null
);

const selectorDateQuery = createSelector([selectorDateFrom, selectorDateTo], (dateFrom, dateTo) => {
    if (dateFrom && dateTo) {
        return {
            from: format(dateFrom, SERVER_FORMAT_DATE),
            to: format(dateTo, SERVER_FORMAT_DATE),
        };
    }
    return {};
});

const selectorDateIsSelected = createSelector([selectorDateFrom, selectorDateTo], (from, to) =>
    Boolean(from && to)
);

const selectorDestinations = createSelector(
    [appSelectors.makeSelectDestinations(), appSelectors.makeSelectCategoriesAsObject()],
    (destinations, categoriesAsObject) =>
        sortBy(
            destinations
                .filter(
                    (destination) =>
                        typeof destination.url_search === 'string' &&
                        destination.url_search.length > 0 &&
                        Array.isArray(destination.categories) &&
                        destination.categories.length > 0
                )
                .map((destination) => ({
                    id: destination.id,
                    url: destination.url,
                    url_search: destination.url_search,
                    name: destination.name,
                    state: destination.state,
                    slug: destination.slug,
                    label: [destination.name, destination.state.code].join(', '),
                    categories: sortBy(
                        destination.categories
                            .filter((category) => !!categoriesAsObject[category.id])
                            .map((category) => ({
                                id: category.id,
                                name: categoriesAsObject[category.id].name,
                                slug: categoriesAsObject[category.id].slug,
                            })),
                        'name'
                    ),
                })),
            'label'
        )
);

const selectorDestination = createSelector(
    [domains.destinationId, selectorDestinations],
    (destinationId, searchDestinations) => {
        const selectedDestinationId = destinationId;
        if (!selectedDestinationId) {
            return null;
        }

        const selectedDestination = searchDestinations.find(
            (destination) => destination.id === selectedDestinationId
        );
        if (!selectedDestination) {
            return null;
        }

        return selectedDestination;
    }
);

const selectorDestinationCategory = createSelector(
    [domains.destinationCategoryId, selectorDestination],
    (destinationCategoryId, selectedDestination) => {
        if (!selectedDestination || !Array.isArray(selectedDestination.categories)) {
            return null;
        }

        const selectedDestinationCategoryId = destinationCategoryId;

        if (!selectedDestinationCategoryId) {
            return null;
        }

        return selectedDestination.categories.find(
            (category) => category.id === selectedDestinationCategoryId
        );
    }
);

const selectorDestinationSlugs = createSelector(
    [selectorDestination, selectorDestinationCategory],
    (destination, category) => ({
        destination: destination && destination.slug,
        category: category && category.slug,
    })
);

const makeSelectDateFrom = () => selectorDateFrom;
const makeSelectDateTo = () => selectorDateTo;
const makeSelectDateQuery = () => selectorDateQuery;
const makeSelectDestinations = () => selectorDestinations;
const makeSelectDestination = () => selectorDestination;
const makeSelectDestinationCategory = () => selectorDestinationCategory;
const makeSelectDateIsSelected = () => selectorDateIsSelected;
const makeSelectDestinationSlugs = () => selectorDestinationSlugs;

export {
    makeSelectDateFrom,
    makeSelectDateTo,
    makeSelectDateQuery,
    makeSelectDestination,
    makeSelectDestinationCategory,
    makeSelectDateIsSelected,
    makeSelectDestinationSlugs,
    makeSelectDestinations,
};
